import c from 'classnames';
import { LexicalEditor, LexicalNode, NodeKey } from 'lexical';
import { CreateTemplatePresetPlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/create-preset-template-placeholder-node';
import { TemplatePlaceholder } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/edit-placeholder-content-overlay';
import { PlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/shared';
import { ContactTextPlaceholderKey } from 'src/components/pages/conversations/components/general/text-placeholders-helpers';

type SerializedTemplatePresetPlaceholderResolverNode = {
  presetKey: ContactTextPlaceholderKey;
  value?: string;
  fallbackValue: string;
  type: 'template-preset-placeholder';
  version: 1;
};

export const $createTemplatePresetPlaceholderResolverNode = ({
  fallbackValue,
  presetKey,
  value,
}: {
  presetKey: ContactTextPlaceholderKey;
  fallbackValue?: string;
  value?: string;
}) => {
  return new TemplatePresetPlaceholderResolver(presetKey, fallbackValue, value);
};

export class TemplatePresetPlaceholderResolver extends CreateTemplatePresetPlaceholderNode {
  __value?: string;
  __hasError: boolean;
  __isActive: boolean;

  constructor(
    presetKey: ContactTextPlaceholderKey,
    fallbackValue?: string,
    value?: string,
    key?: NodeKey,
  ) {
    super({ presetKey, fallbackValue }, key);

    this.__value = value;
    this.__hasError = false;
    this.__isActive = false;
  }

  static clone(
    node: TemplatePresetPlaceholderResolver,
  ): TemplatePresetPlaceholderResolver {
    return new TemplatePresetPlaceholderResolver(
      node.presetKey,
      node.fallbackValue,
      node.value,
      node.__key,
    );
  }

  static getType(): string {
    return 'template-preset-placeholder';
  }

  get value() {
    return this.__value;
  }

  set value(newValue: string | undefined) {
    this.__value = newValue;
  }

  set hasError(newValue: boolean) {
    this.__hasError = newValue;
  }

  get hasError() {
    return this.__hasError;
  }

  set isActive(newValue: boolean) {
    this.__isActive = newValue;
  }

  get isActive() {
    return this.__isActive;
  }

  getTextContent() {
    return this.isValid() ? this.value : this.fallbackValue ?? '';
  }

  isValid(): this is { value: string } {
    return Boolean(this.value && this.value.trim().length > 0);
  }

  decorate(editor: LexicalEditor) {
    return (
      <TemplatePlaceholder
        value={this.value}
        onSave={(value) => {
          editor.update(() => {
            const writable = this.getWritable();

            writable.value = value;
            writable.hasError = false;
          });
        }}
        onRemove={() => {
          editor.update(() => {
            const writable = this.getWritable();

            writable.remove();
          });
        }}
        onPopoverVisibilityChange={(isVisible) => {
          editor.update(() => {
            const writable = this.getWritable();

            writable.isActive = isVisible;
          });
        }}
        isActive={this.isActive}
      >
        <PlaceholderNode
          data-lgg-id="template-preset-placeholder-resolver-node"
          data-preset-key={this.presetKey}
          className={c({ error: this.hasError, active: this.isActive })}
        >
          {this.isValid()
            ? this.value
            : this.fallbackValue && this.fallbackValue?.length > 0
            ? this.fallbackValue
            : `{{${this.presetKey}}}`}
        </PlaceholderNode>
      </TemplatePlaceholder>
    );
  }

  static importJSON(
    serializedNode: SerializedTemplatePresetPlaceholderResolverNode,
  ): LexicalNode {
    return new TemplatePresetPlaceholderResolver(
      serializedNode.presetKey,
      serializedNode.fallbackValue,
      serializedNode.value,
    );
  }

  exportJSON(): SerializedTemplatePresetPlaceholderResolverNode {
    return {
      type: 'template-preset-placeholder',
      presetKey: this.presetKey,
      fallbackValue: this.fallbackValue ?? '',
      value: this.value,
      version: 1,
    };
  }
}
